(function () {
  console.log("Cart Observer activo");

  const OBSERVER_KEY = "__valhallaCartObserverState";
  const POLL_INTERVAL = 800;
  const DEBOUNCE_DELAY = 180;
  const SHIPPING_WAIT_TIMEOUT = 5000;
  const LOCATION_STORAGE_KEY = "customer_location";
  const SHIPPING_SYNC_SOURCE = "valhalla-shipping";

  if (window[OBSERVER_KEY]?.destroy) {
    window[OBSERVER_KEY].destroy();
  }

  let lastCartHash = "";
  let debounceTimer = null;
  let pollTimer = null;
  let domObserver = null;
  let isProcessing = false;
  let pendingCheck = false;
  let pendingForce = false;
  let skipNextShippingCausedChange = false;
  let destroyed = false;

  function getLocationHash() {
    const saved = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!saved) return "";

    try {
      const data = JSON.parse(saved);
      const lat = Number(data?.lat);
      const lng = Number(data?.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "invalid";

      return JSON.stringify({
        lat,
        lng,
        address: data?.address || "",
      });
    } catch (err) {
      return "invalid";
    }
  }

  function buildCartHash(cart) {
    if (!cart?.items) return null;

    return JSON.stringify(
      cart.items.map((item) => ({
        key: item.key,
        id: item.id,
        variant_id: item.variant_id,
        product_id: item.product_id,
        qty: item.quantity,
        shipping_type: item.properties?.shipping_type || item.properties?.["shipping_type"] || null,
      }))
    );
  }

  async function getCartSnapshot() {
    try {
      const res = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        console.warn("Cart no listo aún...");
        return null;
      }

      const cart = await res.json();
      return {
        cart,
        hash: JSON.stringify({
          cart: buildCartHash(cart),
          location: getLocationHash(),
        }),
      };
    } catch (err) {
      console.warn("Error temporal cart (ignorado)");
      return null;
    }
  }

  function waitForShipping() {
    if (window.Shipping?.applyShippingLogic) {
      return Promise.resolve(window.Shipping);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const interval = setInterval(() => {
        if (window.Shipping?.applyShippingLogic) {
          clearInterval(interval);
          resolve(window.Shipping);
          return;
        }

        if (Date.now() - startedAt >= SHIPPING_WAIT_TIMEOUT) {
          clearInterval(interval);
          resolve(null);
        }
      }, 100);
    });
  }

  function shouldForceSync(reason) {
    return [
      "cart-api",
      "cart-mutation",
      "cart-mutated",
      "location",
      "native-cart-update",
      "manual",
    ].some((prefix) => String(reason || "").startsWith(prefix));
  }

  async function runCheck(reason = "scheduled", force = false) {
    if (destroyed) return;

    if (isProcessing) {
      pendingCheck = true;
      pendingForce = pendingForce || force || shouldForceSync(reason);
      return;
    }

    isProcessing = true;

    try {
      let forceReasonForCurrentRun = shouldForceSync(reason);

      do {
        const forceCurrentRun = pendingForce || force || forceReasonForCurrentRun;
        forceReasonForCurrentRun = false;
        pendingCheck = false;
        pendingForce = false;

        const snapshot = await getCartSnapshot();
        if (!snapshot?.hash) continue;

        const changed = Boolean(lastCartHash && lastCartHash !== snapshot.hash);
        const firstSnapshot = !lastCartHash;
        lastCartHash = snapshot.hash;

        if (!changed && !forceCurrentRun && !firstSnapshot) continue;
        if (changed && skipNextShippingCausedChange && !forceCurrentRun) {
          skipNextShippingCausedChange = false;
          continue;
        }

        console.log("Cambio REAL detectado en carrito:", reason);

        const shipping = await waitForShipping();
        if (shipping?.applyShippingLogic) {
          await shipping.applyShippingLogic();
        }
        force = false;
      } while (pendingCheck && !destroyed);
    } finally {
      isProcessing = false;
      document.dispatchEvent(
        new CustomEvent("valhalla:observer-idle", {
          detail: {
            reason,
          },
        })
      );
    }
  }

  function schedule(reason = "scheduled", delay = DEBOUNCE_DELAY, options = {}) {
    if (destroyed) return;

    pendingForce = pendingForce || Boolean(options.force) || shouldForceSync(reason);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      runCheck(reason, pendingForce);
    }, delay);
  }

  function nodeTouchesCart(node) {
    if (!(node instanceof Element)) return false;

    return Boolean(
      node.matches?.("cart-items-component, cart-drawer-component, .cart__ctas, #cart-form") ||
        node.closest?.("cart-items-component, cart-drawer-component, .cart__ctas, #cart-form")
    );
  }

  function mutationTouchesCart(mutation) {
    if (nodeTouchesCart(mutation.target)) return true;

    return [...mutation.addedNodes, ...mutation.removedNodes].some(nodeTouchesCart);
  }

  function startDomObserver() {
    if (!document.body || domObserver) return;

    domObserver = new MutationObserver((mutations) => {
      if (mutations.some(mutationTouchesCart)) {
        schedule("dom-mutation");
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function startPolling() {
    clearInterval(pollTimer);
    pollTimer = setInterval(() => {
      schedule("poll", 0);
    }, POLL_INTERVAL);
  }

  function destroy() {
    destroyed = true;
    clearTimeout(debounceTimer);
    clearInterval(pollTimer);
    domObserver?.disconnect();
  }

  window.CartObserver = {
    schedule,
    checkNow: runCheck,
    isBusy: () => isProcessing || pendingCheck || pendingForce || Boolean(debounceTimer),
    destroy,
  };

  window[OBSERVER_KEY] = window.CartObserver;

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        startDomObserver();
        startPolling();
        runCheck("init", true);
      },
      { once: true }
    );
  } else {
    startDomObserver();
    startPolling();
    runCheck("init", true);
  }

  document.addEventListener("valhalla:cart-mutated", (event) => {
    const reason = event.detail?.reason || "unknown";

    if (String(reason).startsWith("shipping-")) {
      skipNextShippingCausedChange = true;
      schedule(`shipping-cart-mutated:${reason}`, 120, { force: false });
      return;
    }

    schedule(`cart-mutated:${reason}`, 80, { force: true });
  });

  document.addEventListener("valhalla:location-changed", () => {
    schedule("location", 80, { force: true });
  });

  document.addEventListener("valhalla:shipping-updated", (event) => {
    skipNextShippingCausedChange = true;
    schedule(`shipping-updated:${event.detail?.reason || "unknown"}`, 160, { force: false });
  });

  document.addEventListener("cart:update", (event) => {
    if (event.detail?.data?.source === SHIPPING_SYNC_SOURCE) return;
    schedule("native-cart-update", 120, { force: true });
  });

  window.addEventListener("storage", (event) => {
    if (event.key === LOCATION_STORAGE_KEY) {
      schedule("location-storage", 80, { force: true });
    }
  });
})();
